// apps/web/src/pages/login.tsx — B1 Login screen (Phase 2 plan 02-01).
//
// Per 02-UI-SPEC.md B1 layout:
//   - centered card on bg-bg, borderRadius 14, width min(440px, 100%)
//   - heading "Personal-Login" / "Staff Sign-in"
//   - subhead "Ristorante Esempio · Verwaltung / Management"
//   - DE/EN pill toggle (carried over from Phase 1)
//   - two required fields (email + password)
//   - role hint below
//   - primary submit button
//   - inline error helper below password field on failure
//
// On success: useAuth().login() stores the token in memory and
// navigates to /admin/einstellungen.
//
// On failure: the GraphQL error's `extensions.messageKey` is mapped
// through i18next.t() — the web client owns the DE/EN translation.

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { gqlRequest } from "../lib/api";

const LOGIN_MUTATION = /* GraphQL */ `
  mutation LoginPage($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      accessToken
      expiresIn
      user {
        id
        email
        role
      }
    }
  }
`;

interface LoginResponse {
  login: {
    accessToken: string;
    expiresIn: number;
    user: { id: string; email: string; role: "ADMIN" | "SERVICE" | "READONLY" };
  };
}

interface GraphqlErrorResponse {
  response?: {
    errors?: Array<{
      extensions?: { code?: string; messageKey?: string };
    }>;
  };
}

const MESSAGE_KEY_TO_I18N: Record<string, string> = {
  "auth.invalidCredentials": "auth.errors.invalidCredentials",
  "auth.accountLocked": "auth.errors.accountLocked",
  "auth.csrfMismatch": "auth.errors.csrfMismatch",
  "auth.refreshTokenReused": "auth.errors.refreshTokenReused",
  "auth.unauthenticated": "auth.errors.unauthenticated",
  "auth.forbidden": "auth.errors.forbidden",
};

export function LoginPage() {
  const { t, i18n: i18next } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      gqlRequest<LoginResponse>(LOGIN_MUTATION, { email, password }),
    onSuccess: async () => {
      setErrorMessageKey(null);
      await login(email, password);
      navigate("/admin/einstellungen", { replace: true });
    },
    onError: (err: GraphqlErrorResponse) => {
      const extensions = err.response?.errors?.[0]?.extensions;
      const key = extensions?.messageKey ?? "auth.invalidCredentials";
      setErrorMessageKey(
        MESSAGE_KEY_TO_I18N[key] ?? "auth.errors.invalidCredentials",
      );
    },
  });

  const isEnglish = i18next.language.startsWith("en");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex flex-col items-center justify-center px-6 py-12"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      <div
        className="card"
        style={{
          borderRadius: 14,
          width: "min(440px, 100%)",
          padding: "28px 24px",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            aria-label={isEnglish ? "Change language" : "Sprache wechseln"}
            data-testid="lang-toggle"
            onClick={() => {
              const next = isEnglish ? "de" : "en";
              void i18next.changeLanguage(next);
              try {
                window.localStorage.setItem("gastio.lang", next);
              } catch {
                /* localStorage may be unavailable; toggle still works in-memory */
              }
            }}
            className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full border border-divider font-heading text-sm hover:bg-surface"
          >
            {isEnglish ? "EN" : "DE"}
          </button>
        </div>
        <h1
          className="font-heading text-[22px] font-semibold mb-1"
          data-testid="login-title"
        >
          {t("auth.login.title")}
        </h1>
        <p className="text-sm text-neutral-700 mb-6">
          {t("auth.login.subhead")}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm">
              {t("auth.login.email")}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="field"
              data-testid="email-input"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm">
              {t("auth.login.password")}
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="field"
              data-testid="password-input"
            />
          </label>
          {errorMessageKey && (
            <p
              className="text-neutral-700 text-[11px]"
              data-testid="login-error"
              role="alert"
            >
              {t(errorMessageKey)}
            </p>
          )}
          <p className="text-[11px] text-neutral-700">
            {t("auth.login.roleHint")}
          </p>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary btn-block"
            data-testid="login-submit"
          >
            {mutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" aria-hidden />
                {t("auth.login.loading")}
              </span>
            ) : (
              t("auth.login.submit")
            )}
          </button>
        </form>
      </div>
    </main>
  );
}
